/**
 * 칭호 창 (U). 해금된 칭호를 고른다.
 */

import Phaser from 'phaser';
import { Sfx } from '../audio/Sfx';
import { TITLE_ORDER, TITLES, getTitle } from '../data/titles';
import { EV } from '../game/events';
import type { PlayerState } from '../systems/PlayerState';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

const WIDTH = 360;
const HEIGHT = 380;
const PAD = 14;

export class TitleWindow {
  private readonly container: Phaser.GameObjects.Container;
  private readonly rows: Phaser.GameObjects.Container[] = [];
  private readonly worn: Phaser.GameObjects.Text;

  constructor(
    scene: Phaser.Scene,
    private readonly state: PlayerState,
  ) {
    const x = scene.scale.width / 2 - WIDTH / 2 + 80;
    const y = 72;
    this.container = scene.add.container(x, y).setVisible(false).setDepth(30);

    const g = scene.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(-4, -4, WIDTH + 8, HEIGHT + 8, 12);
    g.fillStyle(COLOR.window, 0.98);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 10);
    this.container.add(g);

    const title = scene.add.text(PAD, 10, '칭호', {
      fontFamily: UI_FONT,
      fontSize: '16px',
      color: TEXT_COLOR.dark,
    });
    const close = scene.add
      .text(WIDTH - PAD, 12, '✕', {
        fontFamily: UI_MONO,
        fontSize: '14px',
        color: TEXT_COLOR.dark,
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerdown', () => this.close());
    this.worn = scene.add.text(PAD, 36, '', {
      fontFamily: UI_FONT,
      fontSize: '13px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
    });
    this.container.add([title, close, this.worn]);

    this.state.events.on(EV.titleChanged, this.refresh, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.state.events.off(EV.titleChanged, this.refresh, this);
    });
  }

  toggle(): boolean {
    this.container.setVisible(!this.container.visible);
    if (this.container.visible) this.refresh();
    return this.container.visible;
  }

  close(): void {
    this.container.setVisible(false);
  }

  get visible(): boolean {
    return this.container.visible;
  }

  private refresh = (): void => {
    this.worn.setText(`착용  ${getTitle(this.state.titleId).name}`);
    for (const row of this.rows) row.destroy();
    this.rows.length = 0;
    TITLE_ORDER.forEach((id, i) => {
      const def = TITLES[id];
      if (!def) return;
      const got = this.state.unlockedTitles.includes(id);
      const on = this.state.titleId === id;
      const y = 64 + i * 52;
      const row = this.container.scene.add.container(0, y);
      const hit = this.container.scene.add
        .rectangle(WIDTH / 2, 22, WIDTH - 20, 46, on ? 0xc8b890 : 0xeee2c4, 1)
        .setInteractive({ useHandCursor: got });
      const label = this.container.scene.add.text(PAD + 8, 6, def.name, {
        fontFamily: UI_FONT,
        fontSize: '14px',
        color: got ? TEXT_COLOR.dark : '#8a8a8a',
      });
      const hint = this.container.scene.add.text(PAD + 8, 26, got ? (on ? '착용 중' : '클릭해서 착용') : def.hint, {
        fontFamily: UI_FONT,
        fontSize: '11px',
        color: TEXT_COLOR.darkDim,
      });
      if (got) {
        hit.on('pointerdown', () => {
          if (this.state.equipTitle(id)) Sfx.uiClick();
          else Sfx.deny();
        });
      }
      row.add([hit, label, hint]);
      this.container.add(row);
      this.rows.push(row);
    });
  };
}
